import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import Select from "react-select";
import { Link, useLocation } from "react-router-dom";
import GoodsRequestForm from "./Goodsrequestform";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import MaskedValue from "../../Component/MaskedValue";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";


const USER_MANUAL_SCOPE = {
    moduleName: "Production",
    process: "Shop Floor Control",
    subProcess: "Goods Request"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// Reads module-level permissions (can_view / can_create / can_edit /
// can_convert) for SFC_GOODS_REQUEST from the logged-in user's stored
// permissions — same array authController.js returns at login, same
// pattern Confirmation.js / PlannedOrder.js / ProductionOrder.js use.
// Admins get an empty `permissions` array from the backend by design, so
// they're granted full access here rather than being locked out by an
// "empty permissions" default.
//
// Goods Request only ever needs three of the four flags:
//  - canView    -> DISPLAY: browse/view all three tabs (Active, Requested,
//                  Confirmed) & the View/Docs modal — always available.
//  - canConvert -> CONVERT: on the Active tab, turn selected production-
//                  order lines into a Request (the "Request" button).
//  - canEdit    -> CHANGE: on the Confirmed tab, confirm/issue against an
//                  existing request (the "Confirm" button).
// There is no standalone Create form here, so canCreate is read but
// intentionally unused.
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false, canEdit: false, canConvert: false };
        if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canConvert: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1,
            canEdit: perm?.can_edit === 1,
            canConvert: perm?.can_convert === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false, canEdit: false, canConvert: false };
    }
};

// ══════════════════════════════════════════════════════════
// getCurrentUserId
// Returns the logged-in user's ID (never name/email) — same
// robust fallback chain as PlannedOrder.js's getCurrentUserId,
// so this works regardless of exactly which key the login
// response uses for the user's ID.
// ══════════════════════════════════════════════════════════
const getCurrentUserId = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
        try {
            const parsedUser = JSON.parse(userObj);
            const id = parsedUser.userId ?? parsedUser.user_id ?? parsedUser.id;
            if (id !== undefined && id !== null && id !== "") return id;
        } catch (e) {
            console.error("Error parsing user data:", e);
        }
    }
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
    return "";
};

// ── Rounds a numeric value UP to at most 2 decimal places for display
//    (always rounds up, never down). Leaves non-numeric values
//    (blank, text, etc.) untouched.
const formatQty = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Math.ceil(num * 100) / 100;
};

// ── Converts a "YYYY-MM-DD" (optionally with a "THH:MM:SS" suffix)
//    date string into "DD-MM-YYYY" for display purposes only.
//    Pure string manipulation (no `new Date(...)` involved) so there is
//    no UTC/local timezone conversion and the date can never shift by
//    a day. Anything that isn't a recognisable date is returned as-is.
const toDMY = (value) => {
    if (!value) return value;
    const datePart = String(value).split("T")[0];
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return datePart;
    const [, y, m, d] = match;
    return `${d}-${m}-${y}`;
};

// ── Formats a row's sales_order_numbers value into a single
//    comma-separated display string for use in table cells. The value
//    can arrive either as a JSON array string (e.g. from goods_request,
//    stored as '["SO001","SO002"]') or as a plain GROUP_CONCAT'd comma
//    string (e.g. from the not-yet-requested list) — both are handled.
const formatSalesOrders = (value) => {
    if (!value) return "-";
    let list = value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            list = Array.isArray(parsed) ? parsed : value.split(",");
        } catch (err) {
            list = value.split(",");
        }
    }
    if (!Array.isArray(list)) list = [list];
    const cleaned = list.map((v) => String(v).trim()).filter(Boolean);
    return cleaned.length > 0 ? cleaned.join(", ") : "-";
};

// ── Formats a row's production_order_number value into a plain,
//    comma-separated display string. A clubbed request line stores this
//    as a JSON array string (e.g. '["PRD0008","PRD0007"]'); an
//    un-clubbed / legacy row is just a plain string (e.g. "PRD0005").
//    Both are handled so the UI never shows raw quotes/brackets.
// ── Active-tab checkbox selection rule ────────────────────────────
// Configured in Configuration -> Goods Request Selection Rule. Each
// ticked option means: rows selected together must share the SAME value
// for that field. Options can be combined freely. `max_production_orders`
// caps how many DIFFERENT Production Order Numbers may be selected
// together (null = no limit). Default (nothing configured yet) is the
// original behaviour: same Workcenter only.
const DEFAULT_SELECTION_RULE = {
    same_production_order: false,
    same_product_code: false,
    same_workcenter: true,
    max_production_orders: null,
};

const SELECTION_RULE_FIELDS = [
    { flag: "same_production_order", key: "production_order_number", label: "Production Order Number" },
    { flag: "same_product_code", key: "product_code", label: "Product Code" },
    { flag: "same_workcenter", key: "workcenter_name", label: "Workcenter" },
];

const joinLabels = (list) =>
    list.length <= 1
        ? list.join("")
        : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;

// Returns null when `rows` satisfy the rule, otherwise a user-facing
// warning message.
const getSelectionRuleViolation = (rows, rule) => {
    const mismatched = SELECTION_RULE_FIELDS
        .filter((f) => rule[f.flag])
        .filter((f) => new Set(rows.map((r) => r[f.key])).size > 1)
        .map((f) => f.label);

    if (mismatched.length > 0) {
        return `Select items with the same ${joinLabels(mismatched)} only.`;
    }

    const max = Number(rule.max_production_orders) || 0;
    if (max > 0) {
        const uniquePOs = new Set(rows.map((r) => r.production_order_number)).size;
        if (uniquePOs > max) {
            return `You can select a maximum of ${max} unique Production Order${max === 1 ? "" : "s"} at a time.`;
        }
    }

    return null;
};

const formatProductionOrders = (value) => {
    if (!value) return "-";
    let list = value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            list = Array.isArray(parsed) ? parsed : value.split(",");
        } catch (err) {
            list = value.split(",");
        }
    }
    if (!Array.isArray(list)) list = [list];
    const cleaned = list.map((v) => String(v).trim()).filter(Boolean);
    return cleaned.length > 0 ? cleaned.join(", ") : "-";
};

// ── Sums qty across an array of stock/batch rows for display.
//    Available-stock rows now come from the backend as `qty`, but the
//    Confirmed-tab batch rows still use `issued_qty`, so support both.
const sumQty = (stocks) =>
    (stocks || []).reduce(
        (sum, s) => sum + Number(s.qty ?? s.issued_qty ?? 0),
        0
    );

function GoodsRequest() {
    const location = useLocation();

    // Module-level access for this page — see getModulePermissions above.
    const { canView, canEdit, canConvert } = useMemo(
        () => getModulePermissions("SFC_GOODS_REQUEST"),
        []
    );
    const canSeePage = canView || canEdit || canConvert;

    // Field-level access for this module. Falls open (true) for admins,
    // users with no role assigned yet, or any non-DISPLAY permission type —
    // matching useFieldPermissions' own documented behavior.
    const { isFieldVisible } = useFieldPermissions("SFC_GOODS_REQUEST");

    const [data, setData] = useState([]);
    const [modalType, setModalType] = useState("request");
    const [selectedRows, setSelectedRows] = useState([]);
    // Active-tab selection rule (from Configuration) + last violation message
    const [selectionRule, setSelectionRule] = useState(DEFAULT_SELECTION_RULE);
    const [selectionWarning, setSelectionWarning] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [requestItems, setRequestItems] = useState([]);

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // Tracks whether the user has attempted to Save the Requested-tab
    // modal at least once — used to show red-highlighted missing fields
    // (Request Qty / Storage Location) only after a failed attempt,
    // rather than immediately when the modal opens.
    const [requestAttemptedSave, setRequestAttemptedSave] = useState(false);
    //eslint-disable-next-line
    const [storageLocations, setStorageLocations] = useState([]);
    const [activeTab, setActiveTab] = useState("active");

    // ── Filter dropdown option lists ──────────────────────────────
    const [productTypeOptions, setProductTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);
    const [workcenterOptions, setWorkcenterOptions] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);

    // ── Batch modal state ─────────────────────────────────────────
    const [availableStocks, setAvailableStocks] = useState({});

    // ── Requested-tab table: available stock per row (keyed by request_no + product_code)
    const [rowAvailableStocks, setRowAvailableStocks] = useState({});

    // ── Confirmed-tab: batch grouping + "view all batches" modal ───
    const [batchModalData, setBatchModalData] = useState(null);

    // ── Docs modal: now doubles as the "View" detail modal
    //    (Product tab + Docs tab), opened from the Requested/Confirmed
    //    tab tables ───────────────────────────────────────────────
    const [docsModal, setDocsModal] = useState(null); // { requestNo, title, items, activeDetailTab, autoPrint? }

    // ── Change Log tab data for the View modal ──────────────────────
    const [changeLogs, setChangeLogs] = useState([]);

    // ── Available stock per line item shown in the View modal's
    //    Product tab, keyed by item index ─────────────────────────
    const [detailAvailableStocks, setDetailAvailableStocks] = useState({});

    // ── Auto-print or auto-download when modal opens with a flag ───
    useEffect(() => {
        if (docsModal?.autoPrint) {
            const timer = setTimeout(() => printGoodsRequest(), 1200);
            return () => clearTimeout(timer);
        }
        if (docsModal?.autoDownload) {
            const timer = setTimeout(() => {
                downloadGoodsRequest(`${docsModal.requestNo}.pdf`);
            }, 1200);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docsModal]);

    // ── Opens the shared batch modal showing available stock batches
    //    (batch_number + qty) for a product at a storage location.
    const openStockModal = (productCode, description, storageLabel, stocks) => {
        setBatchModalData({
            product_code: productCode,
            description: description,
            storage_label: storageLabel,
            batches: stocks || [],
        });
    };

    // ── Opens the View modal for a request_no: fetches all line items
    //    for that request plus available stock per line, then shows the
    //    Product tab by default (Docs tab renders the printable form).
    // Change Log — same pattern as PurchaseOrder.js's fetchLogs(poId) /
    // Quality.js's fetchLogs(stockId), keyed here by request_no.
    const fetchLogs = async (requestNo) => {
        try {
            const res = await axios.get(`${PURCHASE_API}/goods-request/logs/${requestNo}`);
            if (res.data.success) {
                setChangeLogs(res.data.logs);
            }
        } catch (err) {
            console.error("Error fetching logs:", err);
        }
    };

    const openDetailModal = async (requestNo) => {
        try {
            const res = await axios.get(
                `${PURCHASE_API}/goods-request/requested/${requestNo}`
            );
            const items = res.data.data || [];

            setDetailAvailableStocks({});

            setDocsModal({
                requestNo,
                title: requestNo,
                items,
                activeDetailTab: "product",
            });

            fetchLogs(requestNo);

            items.forEach((item, idx) => {
                if (item.storage_code) {
                    axios
                        .get(`${PURCHASE_API}/goods-request/available-stock`, {
                            params: {
                                productCode: item.product_code,
                                storageCode: item.storage_code,
                            },
                        })
                        .then((r) => {
                            setDetailAvailableStocks((prev) => ({
                                ...prev,
                                [idx]: r.data.data,
                            }));
                        })
                        .catch((err) =>
                            console.error("Failed to load available stock:", err)
                        );
                }
            });
        } catch (err) {
            console.error("Failed to load request details:", err);
            toast.error("Failed to load request details");
        }
    };

    // ── Downloads the rendered form as a PDF file using html2canvas + jsPDF
    const downloadGoodsRequest = async (filename) => {
        const printArea = document.getElementById("gr-print-area");
        if (!printArea) return;

        try {
            const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);

            const canvas = await html2canvas(printArea, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 20; // 10mm margin each side
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let yOffset = 10;
            let remaining = imgHeight;

            // If content spans multiple pages, slice it
            while (remaining > 0) {
                pdf.addImage(imgData, "PNG", 10, yOffset, imgWidth,
                    Math.min(remaining, pageHeight - 20));
                remaining -= (pageHeight - 20);
                if (remaining > 0) {
                    pdf.addPage();
                    yOffset = 10;
                }
            }

            pdf.save(filename || "GoodsRequest.pdf");
        } catch (err) {
            console.error("PDF download failed:", err);
            toast.error("PDF download failed. Please try Print instead.");
        }
    };

    // ── Prints/downloads by capturing the already-rendered modal content
    //    into a hidden iframe — no new tab, no login redirect.
    const printGoodsRequest = () => {
        const printArea = document.getElementById("gr-print-area");
        if (!printArea) return;

        const styleSheets = Array.from(document.styleSheets)
            .map((ss) => {
                try {
                    return Array.from(ss.cssRules || []).map((r) => r.cssText).join("\n");
                } catch { return ""; }
            })
            .join("\n");

        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;";
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Goods Request</title>
<style>${styleSheets}
body{background:#fff!important;margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;}
@page{size:A4;margin:14mm;}
</style></head><body>${printArea.outerHTML}</body></html>`);
        doc.close();
        iframe.contentWindow.focus();
        setTimeout(() => {
            iframe.contentWindow.print();
            setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 400);
    };




    // ── Active filter values ──────────────────────────────────────
    const [filters, setFilters] = useState({
        productType: null,
        category: null,
        subCategory: null,
        workcenter: null,
        storageLocation: null,
        productionOrder: null,
        fromDate: "",
        toDate: "",
        productCode: null,
    });

    // ── Fetch config dropdown options on mount ────────────────────
    useEffect(() => {
        fetchStorageLocations();
        fetchDropdownOptions();
        fetchProductOptions();
    }, []);

    useEffect(() => {
        setSelectionWarning("");
        if (activeTab === "active") {
            fetchSelectionRule();
            fetchData(filters.fromDate, filters.toDate);
        } else if (activeTab === "requested") {
            fetchRequestedData(filters.fromDate, filters.toDate);
        } else if (activeTab === "confirmed") {
            fetchConfirmedData(filters.fromDate, filters.toDate);
        }
    }, [activeTab, filters.fromDate, filters.toDate]);

    // ── Request modal: auto-load available stock for rows that already
    //    have a storage location set (so it's not blank when the dropdown
    //    onChange never fires, e.g. reopening the modal) ────────────────
    useEffect(() => {
        if (showModal && modalType === "request") {
            requestItems.forEach((item, index) => {
                if (item.storage_code && !availableStocks[index]) {
                    loadAvailableStock(item.product_code, item.storage_code, index);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, modalType, requestItems]);

    // Reads the single Goods Request Selection Rule row. If nothing has
    // been configured (or the call fails) the default rule stays in effect.
    const fetchSelectionRule = async () => {
        try {
            const res = await axios.get(
                `${MASTER_API}/configuration/goods-request-selection-config`
            );
            const row = Array.isArray(res.data) ? res.data[0] : null;
            if (!row) {
                setSelectionRule(DEFAULT_SELECTION_RULE);
                return;
            }
            setSelectionRule({
                same_production_order: !!row.same_production_order,
                same_product_code: !!row.same_product_code,
                same_workcenter: !!row.same_workcenter,
                max_production_orders: row.max_production_orders
                    ? Number(row.max_production_orders)
                    : null,
            });
        } catch (err) {
            console.log(err);
            setSelectionRule(DEFAULT_SELECTION_RULE);
        }
    };

    const openConfirmModal = () => {

        if (selectedRows.length === 0) {
            toast.warning("Please select at least one item");
            return;
        }

        const today = dayjs().format("YYYY-MM-DD");

        const rows = selectedRows.map((item) => ({
            ...item,
            confirm_qty: item.issued_qty ? formatQty(item.issued_qty) : "",
            confirm_date: today,
        }));

        setRequestItems(rows);
        setModalType("confirm");
        setShowModal(true);
    };
    const fetchConfirmedData = async (fromDate, toDate) => {
        try {
            const res = await axios.get(
                `${PURCHASE_API}/goods-request/confirmed`,
                { params: { fromDate, toDate } }
            );

            setData(res.data.data);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchDropdownOptions = async () => {
        try {
            const [productTypes, categories, subCategories, workcenters] =
                await Promise.all([
                    axios.get(`${MASTER_API}/configuration/product-types`),
                    axios.get(`${MASTER_API}/configuration/category`),
                    axios.get(`${MASTER_API}/configuration/sub-category`),
                    axios.get(`${PURCHASE_API}/goods-request/workcenters`),
                ]);

            setProductTypeOptions(
                (productTypes.data || []).map((r) => ({
                    value: r.name || r.id,
                    label: r.name,
                }))
            );

            setCategoryOptions(
                (categories.data || []).map((r) => ({
                    value: r.name || r.id,
                    label: r.name,
                }))
            );

            setSubCategoryOptions(
                (subCategories.data || []).map((r) => ({
                    value: r.name || r.id,
                    label: r.name,
                }))
            );

            setWorkcenterOptions(
                (workcenters.data.data || []).map((r) => ({
                    value: r.workcenter_name,
                    label: r.workcenter_name,
                }))
            );
        } catch (err) {
            console.error("Failed to load filter options:", err);
        }
    };

    const fetchProductOptions = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/goods-request/product-options`);
            const rows = res.data.data || [];

            setProductOptions(
                rows.map((r) => ({
                    value: r.productCode,
                    label: `${r.productCode} - ${r.productDescription}`,
                    description: r.productDescription,
                }))
            );
        } catch (err) {
            console.error("Failed to load product options:", err);
        }
    };

    const loadAvailableStock = async (productCode, storageCode, index) => {
        try {
            const res = await axios.get(
                `${PURCHASE_API}/goods-request/available-stock`,
                { params: { productCode, storageCode } }
            );
            setAvailableStocks((prev) => ({
                ...prev,
                [index]: res.data.data,
            }));
        } catch (err) {
            console.error("Failed to load available stock:", err);
        }
    };

    // ── Loads available stock for a Requested-tab table row, keyed by
    //    request_no + product_code so it doesn't collide with the modal's
    //    index-keyed store above.
    const loadRowAvailableStock = async (productCode, storageCode, key) => {
        try {
            const res = await axios.get(
                `${PURCHASE_API}/goods-request/available-stock`,
                { params: { productCode, storageCode } }
            );
            setRowAvailableStocks((prev) => ({
                ...prev,
                [key]: res.data.data,
            }));
        } catch (err) {
            console.error("Failed to load available stock:", err);
        }
    };

    const fetchRequestedData = async (fromDate, toDate) => {
        try {
            const res = await axios.get(`${PURCHASE_API}/goods-request/requested`, {
                params: { fromDate, toDate },
            });
            setData(res.data.data);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchStorageLocations = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/goods-request/storage-locations`);
            const rows = res.data.data || [];

            setStorageLocations(rows);

            setStorageLocationOptions(
                rows.map((r) => ({
                    value: r.storage_code,
                    label: `${r.storage_code} - ${r.storage_location}`,
                    storage_location: r.storage_location,
                }))
            );
        } catch (err) {
            console.log(err);
        }
    };

    const fetchData = async (fromDate, toDate) => {
        try {
            const res = await axios.get(`${PURCHASE_API}/goods-request/goods-requests`, {
                params: { fromDate, toDate },
            });
            setData(res.data.data);
        } catch (err) {
            console.log(err);
        }
    };

    // ── Derive unique Production Order / Product Code / Description
    //    options from the fetched table data (no extra API call needed)
    const productionOrderOptions = useMemo(
        () =>
            [...new Set((data || []).map((r) => r.production_order_number).filter(Boolean))].map(
                (v) => ({ value: v, label: v })
            ),
        [data]
    );

    const handleSaveConfirm = async () => {
        try {

            // requestItems are built from confirmedGroupedData, which merges
            // every batch issued under one issue_no into a single row and
            // only keeps a single top-level batch_number. If we post
            // requestItems as-is, only the first batch of a multi-batch
            // issue gets confirmed. Expand each row back into one entry
            // per batch (using its .batches array) before posting.
            const payload = requestItems.flatMap((item) => {
                const batches =
                    item.batches && item.batches.length > 0
                        ? item.batches
                        : [
                            {
                                batch_number: item.batch_number,
                                issued_qty: item.issued_qty,
                            },
                        ];

                return batches.map((b) => ({
                    ...item,
                    batch_number: b.batch_number,
                    issued_qty: b.issued_qty,
                    confirmed_by: getCurrentUserId(),
                }));
            });

            await axios.post(
                `${PURCHASE_API}/goods-request/confirm`,
                payload
            );

            toast.success("Confirmed Successfully");

            setShowModal(false);
            setSelectedRows([]);

            fetchConfirmedData(filters.fromDate, filters.toDate);

        } catch (err) {
            console.log(err);
            toast.error("Confirmation Failed");
        }
    };

    // ── Client-side filter applied to table rows ──────────────────
    const filteredData = useMemo(() => {
        return (data || []).filter((item) => {
            if (
                filters.productionOrder &&
                item.production_order_number !== filters.productionOrder.value
            )
                return false;

            if (filters.productCode && item.product_code !== filters.productCode.value)
                return false;

            if (filters.workcenter && item.workcenter_name !== filters.workcenter.value)
                return false;

            if (filters.productType && item.product_type_name !== filters.productType.value)
                return false;

            if (filters.category && item.category_name !== filters.category.value)
                return false;

            if (filters.subCategory && item.sub_category_name !== filters.subCategory.value)
                return false;

            if (
                filters.storageLocation &&
                item.storage_location !== filters.storageLocation.value
            )
                return false;

            return true;
        });
    }, [data, filters]);

    // ── Confirmed tab: the API returns one row per batch issued
    //    against a request (same issue_no/product_code repeated for
    //    every batch). Group those rows into a single row per
    //    issue/product, summing the issued qty and collecting the
    //    individual batches so they can be shown in the Confirm modal
    //    instead of as duplicate table rows.
    const confirmedGroupedData = useMemo(() => {
        if (activeTab !== "confirmed") return filteredData;

        const groups = new Map();

        filteredData.forEach((item) => {
            const key = [
                item.issue_no || "",
                item.request_no || "",
                item.production_order_number || "",
                item.product_code || "",
            ].join("||");

            if (!groups.has(key)) {
                groups.set(key, {
                    ...item,
                    issued_qty: 0,
                    batches: [],
                });
            }

            const group = groups.get(key);

            if (item.batch_number) {
                group.batches.push({
                    batch_number: item.batch_number,
                    issued_qty: item.issued_qty,
                });
            }

            group.issued_qty =
                Number(group.issued_qty || 0) + Number(item.issued_qty || 0);
        });

        return Array.from(groups.values());
    }, [filteredData, activeTab]);

    // ── Requested tab: one request_no can have several product lines
    //    (same request_no repeated per line). Group into a single row
    //    per request_no for the summary table — line-level detail now
    //    lives in the View modal's Product tab.
    const requestedGroupedData = useMemo(() => {
        if (activeTab !== "requested") return filteredData;

        const groups = new Map();

        filteredData.forEach((item) => {
            const key = item.request_no || "";
            if (!groups.has(key)) {
                groups.set(key, { ...item });
            }
        });

        return Array.from(groups.values());
    }, [filteredData, activeTab]);

    // ── Rows actually rendered in the main table. Identical to
    //    filteredData except on the Confirmed tab (batch rows grouped)
    //    and the Requested tab (line rows grouped by request_no).
    const tableData =
        activeTab === "confirmed"
            ? confirmedGroupedData
            : activeTab === "requested"
                ? requestedGroupedData
                : filteredData;

    // ── Builds a unique identity for a row. On the Active tab, production_order
    //    + product_code is unique. On the Confirmed tab, issue_no + request_no
    //    + production_order + product_code is unique now that batch rows are
    //    grouped together into a single row.
    const getRowKey = (item) =>
        [
            item.issue_no || "",
            item.request_no || "",
            item.production_order_number,
            item.product_code,
        ].join("||");

    // ── Requested & Confirmed tabs: auto-load available stock per row,
    //    reusing the existing available-stock endpoint.
    useEffect(() => {
        if (activeTab === "requested") {
            data.forEach((item) => {
                const key = getRowKey(item);
                if (item.storage_code && !rowAvailableStocks[key]) {
                    loadRowAvailableStock(item.product_code, item.storage_code, key);
                }
            });
        } else if (activeTab === "confirmed") {
            tableData.forEach((item) => {
                const key = getRowKey(item);
                if (item.to_storage_code && !rowAvailableStocks[key]) {
                    loadRowAvailableStock(item.product_code, item.to_storage_code, key);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, data, tableData]);

    // ── Styles ────────────────────────────────────────────────────
    const styles = {
        modalBackdrop: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
        },
        modal: {
            background: "#fff",
            borderRadius: "8px",
            maxHeight: "85vh",
            width: "95%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        },
        modalHeader: {
            padding: "12px 20px",
            borderBottom: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
        },
        modalBody: {
            padding: "20px",
            overflow: "auto",
            flex: 1,
        },
        modalFooter: {
            padding: "12px 20px",
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
        },
        iconBtn: {
            background: "transparent",
            border: "none",
            fontSize: "16px",
            cursor: "pointer",
        },
        compactInput: {
            fontSize: "13px",
            height: "32px",
            padding: "4px 8px",
        },
        filterCard: {
            background: "#fff",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            marginBottom: "15px",
        },
        compactLabel: {
            fontSize: "12px",
            fontWeight: "600",
            color: "#334155",
            marginBottom: "4px",
            display: "block",
        },
        readOnlyBox: {
            fontSize: "13px",
            color: "#1e293b",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            padding: "6px 10px",
            minHeight: "32px",
            display: "flex",
            alignItems: "center",
        },
        pageTitleBar: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 15,
        },
        pageTitle: {
            fontSize: "22px",
            fontWeight: "700",
            color: "#1e293b",
        },
        pageSubtitle: {
            fontSize: "13px",
            color: "#64748b",
        },
        tableContainer: {
            background: "#fff",
            borderRadius: 8,
            padding: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            overflowX: "auto",
        },
        erpTable: {
            width: "100%",
            minWidth: "1400px",
            borderCollapse: "collapse",
        },
        erpTh: {
            background: "#f8fafc",
            fontSize: 12,
            fontWeight: 600,
            padding: 10,
            borderBottom: "1px solid #e5e7eb",
            color: "#1e293b",
            whiteSpace: "nowrap",
        },
        erpTd: {
            fontSize: 13,
            padding: 10,
            borderBottom: "1px solid #f1f5f9",
            color: "#334155",
        },
        modalTd: {
            fontSize: 13,
            padding: 10,
            borderBottom: "1px solid #f1f5f9",
            color: "#334155",
            whiteSpace: "nowrap",
        },
        modalInput: {
            minWidth: "90px",
            width: "90px",
            fontSize: "13px",
            height: "32px",
            padding: "4px 8px",
        },
        modalDateInput: {
            minWidth: "130px",
            width: "130px",
            fontSize: "13px",
            height: "32px",
            padding: "4px 8px",
        },
        availableStockCell: {
            whiteSpace: "nowrap",
            minWidth: "200px",
            fontSize: 12,
            fontWeight: 600,
        },
        availableStockQty: {
            fontSize: 15,
            fontWeight: 400,
            color: "#1e293b",
        },
        cellLink: {
            color: "#2563eb",
            textDecoration: "none",
            fontWeight: 600,
            cursor: "pointer",
        },
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "38px",
            height: "38px",
            fontSize: "13px",
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: "38px",
            padding: "0 8px",
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: "38px",
        }),
        option: (provided) => ({
            ...provided,
            fontSize: "12px",
            padding: "6px 12px",
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: "12px",
        }),
    };

    const modalSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "32px",
            height: "32px",
            fontSize: "13px",
            minWidth: "150px",
            width: "150px",
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: "32px",
            padding: "0 8px",
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: "32px",
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2000,
        }),
        option: (provided) => ({
            ...provided,
            fontSize: "12px",
            padding: "6px 12px",
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: "13px",
        }),
    };

    // ── Handlers ──────────────────────────────────────────────────
    // ── Builds a unique identity for a row. On the Active tab, production_order
    //    + product_code is unique. On the Confirmed tab, issue_no + request_no
    //    + production_order + product_code is unique now that batch rows are
    //    grouped together into a single row.
    const handleSelect = (item) => {
        const key = getRowKey(item);
        const exists = selectedRows.find((x) => getRowKey(x) === key);

        if (exists) {
            setSelectedRows(selectedRows.filter((x) => getRowKey(x) !== key));
            setSelectionWarning("");
            return;
        }

        // ── Active tab: apply the configured selection rule (any
        //    combination of same Production Order / Product Code /
        //    Workcenter, plus the max unique Production Orders limit).
        if (activeTab === "active") {
            const violation = getSelectionRuleViolation(
                [...selectedRows, item],
                selectionRule
            );
            if (violation) {
                setSelectionWarning(violation);
                toast.warning(violation);
                return;
            }
            setSelectionWarning("");
            setSelectedRows([...selectedRows, item]);
            return;
        }

        // ── Workcenter-based selection rule (replaces the old
        //    "same Production Order only" rule): rows from different
        //    Production Orders CAN be selected together as long as they
        //    all belong to the same Workcenter. Mixing Workcenters is
        //    still blocked, same as before.
        if (
            selectedRows.length > 0 &&
            selectedRows[0].workcenter_name !== item.workcenter_name
        ) {
            toast.warning(
                "Select items from the same Workcenter only. Different Production Orders under the same Workcenter are fine."
            );
            return;
        }

        setSelectedRows([...selectedRows, item]);
    };

    // const handleSelectAll = (e) => {
    //     if (e.target.checked) {
    //         setSelectedRows(
    //             tableData.filter((item) => item.confirm_status !== "Confirmed")
    //         );
    //     } else {
    //         setSelectedRows([]);
    //     }
    // };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const selectableRows = tableData.filter(
                (item) => item.confirm_status !== "Confirmed"
            );

            // ── Active tab: Select All follows the configured rule too.
            if (activeTab === "active") {
                const violation = getSelectionRuleViolation(selectableRows, selectionRule);
                if (violation) {
                    const msg = `Select All is not allowed. ${violation}`;
                    setSelectionWarning(msg);
                    toast.warning(msg);
                    setSelectedRows([]);
                    return;
                }
                setSelectionWarning("");
                setSelectedRows(selectableRows);
                return;
            }

            // ── Other tabs (Confirmed): unchanged — Workcenter only.
            const workcenterNames = [
                ...new Set(selectableRows.map(item => item.workcenter_name))
            ];

            if (workcenterNames.length > 1) {
                alert("Select All is not allowed because multiple Workcenters exist.");
                setSelectedRows([]);
                return;
            }

            setSelectedRows(selectableRows);
        } else {
            setSelectedRows([]);
            setSelectionWarning("");
        }
    };
    const openRequestModal = () => {
        if (selectedRows.length === 0) {
            toast.warning("Please select at least one item");
            return;
        }

        const today = dayjs().format("YYYY-MM-DD");

        // ── Club rows that share the same Workcenter + Product Code into
        //    a single request line: qty is summed across them, and every
        //    contributing Production Order is kept (not dropped) inside
        //    `production_order_numbers` — one array per line — so the
        //    form always shows every PO that fed into that line, even
        //    though there's only one row for the product.
        const groupedMap = new Map();

        selectedRows.forEach((item) => {
            const groupKey = `${item.workcenter_name}||${item.product_code}`;
            const existing = groupedMap.get(groupKey);

            if (existing) {
                existing.qty = Number(existing.qty || 0) + Number(item.qty || 0);
                if (!existing.production_order_numbers.includes(item.production_order_number)) {
                    existing.production_order_numbers.push(item.production_order_number);
                }
            } else {
                groupedMap.set(groupKey, {
                    ...item,
                    qty: Number(item.qty || 0),
                    production_order_numbers: [item.production_order_number],
                });
            }
        });

        const rows = [...groupedMap.values()].map((item) => ({
            ...item,
            // Comma-joined view for display/table cells that still read
            // the old singular `production_order_number` field.
            production_order_number: item.production_order_numbers.join(", "),
            request_qty: "",
            request_date: today,
            storage_code: "",
            storage_location: "",
            created_by: getCurrentUserId(),
        }));

        setRequestItems(rows);
        setAvailableStocks({});
        setModalType("request");
        setShowModal(true);
        setRequestAttemptedSave(false);
    };

    const handleSaveRequest = async () => {
        const missingQtyRows = requestItems.some(
            (item) => !item.request_qty || Number(item.request_qty) <= 0
        );
        const missingLocationRows = requestItems.some((item) => !item.storage_location);

        if (missingQtyRows || missingLocationRows) {
            setRequestAttemptedSave(true);
            if (missingQtyRows) {
                toast.warning("Please enter Request Qty for all rows");
            } else {
                toast.warning("Please select Storage Location for all rows");
            }
            return;
        }
        try {
            const res = await axios.post(`${PURCHASE_API}/goods-request/save`, requestItems);
            toast.success(res.data.message);
            setShowModal(false);
            setSelectedRows([]);
            fetchData(filters.fromDate, filters.toDate);
        } catch (err) {
            console.log(err);
            toast.error("Failed to save");
        }
    };

    // A non-admin user with no DISPLAY/CHANGE/CONVERT entry for this
    // module (either no role assigned at all, or a role that simply
    // doesn't include Goods Request) has canView/canEdit/canConvert all
    // false, per getModulePermissions above — same convention Confirmation,
    // Planned Order, and Production Order use. Admins always pass via the
    // explicit user.role === "admin" branch.
    if (!canSeePage) {
        return (
            <Layout>
                <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
                    You don't have access to Goods Request. Contact your administrator if you believe this is a mistake.
                </div>
            </Layout>
        );
    }

    // ── Render ────────────────────────────────────────────────────
    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <h2 style={styles.pageTitle}>Goods Request</h2>
                    <p style={styles.pageSubtitle}>Raw Material Requirements</p>
                </div>
                <button
                    onClick={() => {
                        setManualExists(null);
                        setManualFile(null);
                        setShowManual(true);
                        fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                            .then((res) => res.json())
                            .then((data) => {
                                const norm = (v) => String(v || "").trim().toLowerCase();
                                const match = (data?.data || []).find((m) =>
                                    norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                    norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                    norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                );
                                if (match && match.drive_link) {
                                    setManualFile(match);
                                    setManualExists(true);
                                } else {
                                    setManualExists(false);
                                }
                            })
                            .catch(() => setManualExists(false));
                    }}
    title="View User Manual"
    style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        color: "#3b82f6",
        fontSize: "14px",
        fontWeight: "600",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
>
    <FileText size={18} />
    User Manual
</button>
            </div>

            {/* --- User Manual Popup Modal --- */}
{showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "#ffffff", borderRadius: "12px", width: "90vw", height: "97vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
      }}
    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
      }}>
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none", background: "#f1f5f9", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
            color: "#475569", lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25; // 1.0 = normal, 1.25 = 25% bigger, 1.5 = 50% bigger
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
    </div>
  </div>
)}

            {/* <div style={styles.filterCard}>

                <div className="row g-3">
                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Product Type</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={productTypeOptions}
                            value={filters.productType}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productType: val }))
                            }
                            isClearable
                        />
                    </div>
                </div>

                <div className="row g-3 mt-1">
                    <div className="col">
                        <label style={styles.compactLabel}>Category</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={categoryOptions}
                            value={filters.category}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, category: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Sub Category</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={subCategoryOptions}
                            value={filters.subCategory}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, subCategory: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Workcenter</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={workcenterOptions}
                            value={filters.workcenter}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, workcenter: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>To Storage Location</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={storageLocationOptions}
                            value={filters.storageLocation}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, storageLocation: val }))
                            }
                            isClearable
                        />
                    </div>
                </div>

                <div className="row g-3 mt-1">
                    <div className="col">
                        <label style={styles.compactLabel}>Production Order</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={productionOrderOptions}
                            value={filters.productionOrder}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productionOrder: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Product Code</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Search by code or description..."
                            options={productOptions}
                            value={filters.productCode}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productCode: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>From Date</label>
                        <input
                            type="date"
                            className="form-control"
                            style={styles.compactInput}
                            value={filters.fromDate}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
                            }
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>To Date</label>
                        <input
                            type="date"
                            className="form-control"
                            style={styles.compactInput}
                            value={filters.toDate}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
                            }
                        />
                    </div>
                </div>
            </div> */}


            {/* ── Filter Card ──────────────────────────────────── */}
            <div style={styles.filterCard}>

                {/* Row 1 – Product Type */}
                <div className="row g-3">
                    <div className="col-md-3">
                        <label style={styles.compactLabel}>Product Type</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={productTypeOptions}
                            value={filters.productType}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productType: val }))
                            }
                            isClearable
                        />
                    </div>
                </div>

                {/* Row 2 – Category / Sub Category / Workcenter / Storage Location / Product Code / Production Order */}
                <div className="row g-3 mt-1">
                    <div className="col">
                        <label style={styles.compactLabel}>Category</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={categoryOptions}
                            value={filters.category}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, category: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Sub Category</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={subCategoryOptions}
                            value={filters.subCategory}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, subCategory: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Workcenter</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={workcenterOptions}
                            value={filters.workcenter}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, workcenter: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>To Storage Location</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={storageLocationOptions}
                            value={filters.storageLocation}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, storageLocation: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Product Code</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={productOptions}
                            value={filters.productCode}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productCode: val }))
                            }
                            isClearable
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>Production Order</label>
                        <Select
                            styles={selectStyles}
                            placeholder="Select..."
                            options={productionOrderOptions}
                            value={filters.productionOrder}
                            onChange={(val) =>
                                setFilters((prev) => ({ ...prev, productionOrder: val }))
                            }
                            isClearable
                        />
                    </div>
                </div>

                {/* Row 3 – From Date / To Date (commented out)
                <div className="row g-3 mt-1">
                    <div className="col">
                        <label style={styles.compactLabel}>From Date</label>
                        <input
                            type="date"
                            className="form-control"
                            style={styles.compactInput}
                            value={filters.fromDate}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
                            }
                        />
                    </div>

                    <div className="col">
                        <label style={styles.compactLabel}>To Date</label>
                        <input
                            type="date"
                            className="form-control"
                            style={styles.compactInput}
                            value={filters.toDate}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
                            }
                        />
                    </div>
                </div>
                */}
            </div>

            {/* ── Tab Toggle + Request Button ───────────────────── */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                }}
            >
                <div className="gr-tab-toggle">
                    <button
                        className={activeTab === "active" ? "active-tab" : ""}
                        onClick={() => setActiveTab("active")}
                    >
                        Active
                    </button>
                    <button
                        className={activeTab === "requested" ? "active-tab" : ""}
                        onClick={() => setActiveTab("requested")}
                    >
                        Requested
                    </button>
                    <button
                        className={activeTab === "confirmed" ? "active-tab" : ""}
                        onClick={() => setActiveTab("confirmed")}
                    >
                        Confirmed
                    </button>
                </div>

                {activeTab === "active" && canConvert && (
                    <button className="btn btn-dark" onClick={openRequestModal}>
                        Request
                    </button>
                )}

                {activeTab === "confirmed" && canEdit && (
                    <button className="btn btn-dark" onClick={openConfirmModal}>
                        Confirm
                    </button>
                )}

            </div>

            {/* ── Active tab: selection rule hint + last violation ─ */}
            {activeTab === "active" && canConvert && (
                <div style={{ marginBottom: "10px" }}>
                    {selectionWarning && (
                        <div
                            role="alert"
                            style={{
                                marginTop: "6px",
                                padding: "8px 12px",
                                fontSize: "13px",
                                color: "#92400e",
                                background: "#fffbeb",
                                border: "1px solid #fcd34d",
                                borderRadius: "6px",
                            }}
                        >
                            ⚠ {selectionWarning}
                        </div>
                    )}
                </div>
            )}

            {/* ── Main Table ────────────────────────────────────── */}
            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            {/* ── Active tab: checkbox, Production Order, Workcenter,
                                Start Date, End Date, Product Code, Description, UOM, Qty */}
                            {activeTab === "active" && (
                                <>
                                    <th style={styles.erpTh}>
                                        {canConvert && (
                                            <input
                                                type="checkbox"
                                                checked={
                                                    tableData.length > 0 &&
                                                    selectedRows.length === tableData.length
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        )}
                                    </th>
                                    <th style={styles.erpTh}>Production Order</th>
                                    <th style={styles.erpTh}>Sales Order</th>
                                    <th style={styles.erpTh}>Workcenter</th>
                                    {/* <th style={styles.erpTh}>Start Date</th>
                                    <th style={styles.erpTh}>End Date</th> */}
                                    <th style={styles.erpTh}>Product Code</th>
                                    <th style={styles.erpTh}>Description</th>
                                    <th style={styles.erpTh}>UOM</th>
                                    <th style={styles.erpTh}>Actual Qty</th>
                                </>
                            )}

                            {/* ── Requested tab: summary columns only — one row per
                                request_no. Line-level detail (Product Code, Description,
                                UOM, Qty, Requested Qty, Requested Date, Available Stock,
                                To Storage Location, Issued Qty, Issued Date, From Storage
                                Location) now lives in the View modal's Product tab. */}
                            {activeTab === "requested" && (
                                <>
                                    <th style={styles.erpTh}>Request No</th>
                                    <th style={styles.erpTh}>Production Order</th>
                                    <th style={styles.erpTh}>Sales Order</th>
                                    <th style={styles.erpTh}>Workcenter</th>
                                    {/* <th style={styles.erpTh}>Start Date</th>
                                    <th style={styles.erpTh}>End Date</th> */}
                                    <th style={styles.erpTh}>View</th>
                                </>
                            )}

                            {/* ── Confirmed tab: checkbox, Issue No, Request No, Production Order,
                                Workcenter, Start Date, End Date, UOM, Qty, Requested Qty,
                                Requested Date, Available Stock, To Storage Location, Issued Qty,
                                Issued Date, From Storage Location, Status */}
                            {activeTab === "confirmed" && (
                                <>
                                    <th style={styles.erpTh}>
                                        {canEdit && (
                                            <input
                                                type="checkbox"
                                                checked={
                                                    (() => {
                                                        const selectable = tableData.filter(
                                                            (item) => item.confirm_status !== "Confirmed"
                                                        );
                                                        return (
                                                            selectable.length > 0 &&
                                                            selectedRows.length === selectable.length
                                                        );
                                                    })()
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        )}
                                    </th>
                                    <th style={styles.erpTh}>Issue No</th>
                                    <th style={styles.erpTh}>Request No</th>
                                    <th style={styles.erpTh}>Production Order</th>
                                    <th style={styles.erpTh}>Sales Order</th>
                                    <th style={styles.erpTh}>Workcenter</th>
                                    {/* <th style={styles.erpTh}>Start Date</th>
                                    <th style={styles.erpTh}>End Date</th> */}
                                    <th style={styles.erpTh}>Product Code</th>
                                    <th style={styles.erpTh}>Description</th>
                                    <th style={styles.erpTh}>UOM</th>
                                    <th style={styles.erpTh}>Actual Qty</th>
                                    <th style={styles.erpTh}>Requested Qty</th>
                                    <th style={styles.erpTh}>Requested Date</th>
                                    <th style={styles.erpTh}>Available Stock</th>
                                    <th style={styles.erpTh}>To Storage Location</th>
                                    <th style={styles.erpTh}>Issued Qty</th>
                                    <th style={styles.erpTh}>Issued Date</th>
                                    <th style={styles.erpTh}>From Storage Location</th>
                                    <th style={styles.erpTh}>Status</th>
                                    <th style={styles.erpTh}>View</th>
                                </>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {tableData.map((item, index) => {
                            const rowKey = getRowKey(item) || index;
                            const stockForRow = rowAvailableStocks[getRowKey(item)] || [];

                            return (
                                <tr key={rowKey}>
                                    {/* ── Active tab row ─────────────────────────── */}
                                    {activeTab === "active" && (
                                        <>
                                            <td style={styles.erpTd}>
                                                {canConvert && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.some(
                                                            (x) => getRowKey(x) === getRowKey(item)
                                                        )}
                                                        onChange={() => handleSelect(item)}
                                                    />
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('production_order_number') ? <MaskedValue /> : formatProductionOrders(item.production_order_number)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('sales_order_numbers') ? <MaskedValue /> : formatSalesOrders(item.sales_order_numbers)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('workcenter_name') ? <MaskedValue /> : (
                                                    <Link
                                                        to={`/cable/master-data/workcenter?view=${encodeURIComponent(
                                                            item.workcenter_name
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.workcenter_name}
                                                    </Link>
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('product_code') ? <MaskedValue /> : (
                                                    <Link
                                                        to={`/cable/product/product-code?view=${encodeURIComponent(
                                                            item.product_code
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.product_code}
                                                    </Link>
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('description') ? <MaskedValue /> : item.description}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('uom') ? <MaskedValue /> : item.uom}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('qty') ? <MaskedValue /> : formatQty(item.qty)}
                                            </td>
                                        </>
                                    )}

                                    {/* ── Requested tab row (summary — one per request_no) ─ */}
                                    {activeTab === "requested" && (
                                        <>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('request_no') ? <MaskedValue /> : item.request_no}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('production_order_number') ? <MaskedValue /> : formatProductionOrders(item.production_order_number)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('sales_order_numbers') ? <MaskedValue /> : formatSalesOrders(item.sales_order_numbers)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('workcenter_name') ? <MaskedValue /> : (
                                                    <Link
                                                        to={`/cable/master-data/workcenter?view=${encodeURIComponent(
                                                            item.workcenter_name
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.workcenter_name}
                                                    </Link>
                                                )}
                                            </td>
                                            {/* <td style={styles.erpTd}>
                                                {toDMY(item.production_start_date)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {toDMY(item.production_end_date)}
                                            </td> */}
                                            <td style={styles.erpTd}>
                                                <button
                                                    className="btn btn-sm btn-dark"
                                                    style={{ fontSize: "11px", padding: "2px 8px" }}
                                                    onClick={() => openDetailModal(item.request_no)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </>
                                    )}

                                    {/* ── Confirmed tab row ──────────────────────── */}
                                    {activeTab === "confirmed" && (
                                        <>
                                            <td style={styles.erpTd}>
                                                {canEdit && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRows.some(
                                                            (x) => getRowKey(x) === getRowKey(item)
                                                        )}
                                                        disabled={item.confirm_status === "Confirmed"}
                                                        onChange={() => handleSelect(item)}
                                                        style={
                                                            item.confirm_status === "Confirmed"
                                                                ? { cursor: "not-allowed" }
                                                                : undefined
                                                        }
                                                        title={
                                                            item.confirm_status === "Confirmed"
                                                                ? "Already confirmed"
                                                                : undefined
                                                        }
                                                    />
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('issue_no') ? <MaskedValue /> : item.issue_no}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('request_no') ? <MaskedValue /> : item.request_no}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('production_order_number') ? <MaskedValue /> : formatProductionOrders(item.production_order_number)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('sales_order_numbers') ? <MaskedValue /> : formatSalesOrders(item.sales_order_numbers)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('workcenter_name') ? <MaskedValue /> : (
                                                    <Link
                                                        to={`/cable/master-data/workcenter?view=${encodeURIComponent(
                                                            item.workcenter_name
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.workcenter_name}
                                                    </Link>
                                                )}
                                            </td>
                                            {/* <td style={styles.erpTd}>
                                                {toDMY(item.production_start_date)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {toDMY(item.production_end_date)}
                                            </td> */}
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('product_code') ? <MaskedValue /> : (
                                                    <Link
                                                        to={`/cable/product/product-code?view=${encodeURIComponent(
                                                            item.product_code
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.product_code}
                                                    </Link>
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('description') ? <MaskedValue /> : item.description}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('uom') ? <MaskedValue /> : item.uom}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('qty') ? <MaskedValue /> : formatQty(item.qty)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('request_qty') ? <MaskedValue /> : formatQty(item.request_qty)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('request_date') ? <MaskedValue /> : toDMY(item.request_date)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('available_stock') ? <MaskedValue /> : (
                                                    stockForRow.length > 0
                                                        ? formatQty(sumQty(stockForRow))
                                                        : "-"
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('to_storage_location') ? <MaskedValue /> : (
                                                    item.to_storage_location ? (
                                                        <span
                                                            style={styles.cellLink}
                                                            onClick={() =>
                                                                openStockModal(
                                                                    item.product_code,
                                                                    item.description,
                                                                    item.to_storage_location,
                                                                    stockForRow
                                                                )
                                                            }
                                                        >
                                                            {item.to_storage_location}
                                                        </span>
                                                    ) : (
                                                        "-"
                                                    )
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('issued_qty') ? <MaskedValue /> : formatQty(item.issued_qty)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('issued_date') ? <MaskedValue /> : toDMY(item.issue_date)}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('from_storage_location') ? <MaskedValue /> : item.from_storage_location}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('confirm_status') ? <MaskedValue /> : (
                                                    <span
                                                        style={{
                                                            background:
                                                                item.confirm_status === "Confirmed"
                                                                    ? "#dcfce7"
                                                                    : "#fef3c7",
                                                            color:
                                                                item.confirm_status === "Confirmed"
                                                                    ? "#166534"
                                                                    : "#92400e",
                                                            padding: "4px 10px",
                                                            borderRadius: "12px",
                                                            fontWeight: "600",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {item.confirm_status}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                <button
                                                    className="btn btn-sm btn-dark"
                                                    style={{ fontSize: "11px", padding: "2px 8px" }}
                                                    onClick={() => openDetailModal(item.request_no)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── Request Modal ─────────────────────────────────── */}
            {showModal && (
                <div style={styles.modalBackdrop}>
                    <div
                        style={{
                            ...styles.modal,
                            maxWidth: "1300px",
                        }}
                    >
                        <div style={styles.modalHeader}>
                            <h3
                                style={{
                                    margin: 0,
                                    color: "#1e293b",
                                    fontSize: "18px",
                                    fontWeight: 600,
                                }}
                            >
                                {modalType === "request"
                                    ? "Goods Request"
                                    : "Goods Confirmation"}
                            </h3>
                            <button onClick={() => setShowModal(false)} style={styles.iconBtn}>
                                ✖
                            </button>
                        </div>

                        <div style={styles.modalBody}>
                            <div className="card p-8 mb-4 bg-light border-0" style={{ overflowX: "auto" }}>
                                <table className="table" style={{ ...styles.erpTable, minWidth: "1400px" }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Production Order</th>
                                            <th style={styles.erpTh}>Workcenter</th>
                                            {/* <th style={styles.erpTh}>Start Date</th>
                                            <th style={styles.erpTh}>End Date</th> */}
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Description</th>
                                            <th style={styles.erpTh}>UOM</th>
                                            <th style={styles.erpTh}>Actual Qty</th>

                                            {modalType === "request" ? (
                                                <>
                                                    <th style={styles.erpTh}>Request Qty <span style={{ color: "#dc2626" }}>*</span></th>
                                                    <th style={styles.erpTh}>Request Date</th>
                                                    <th style={styles.erpTh}>Available Stock</th>
                                                    <th style={styles.erpTh}>Storage Location <span style={{ color: "#dc2626" }}>*</span></th>
                                                </>
                                            ) : (
                                                <>
                                                    <th style={styles.erpTh}>Requested Qty</th>
                                                    <th style={styles.erpTh}>Requested Date</th>
                                                    <th style={styles.erpTh}>To Storage Location</th>
                                                    <th style={styles.erpTh}>Available Stock</th>
                                                    <th style={styles.erpTh}>Issued Qty</th>
                                                    <th style={styles.erpTh}>Batch No</th>
                                                    <th style={styles.erpTh}>Confirm Qty</th>
                                                    <th style={styles.erpTh}>Confirm Date</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requestItems.map((item, index) => (
                                            <tr key={index}>

                                                <td style={styles.modalTd}>
                                                    {formatProductionOrders(item.production_order_number)}
                                                </td>

                                                <td style={styles.modalTd}>
                                                    <Link
                                                        to={`/cable/master-data/workcenter?view=${encodeURIComponent(
                                                            item.workcenter_name
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.workcenter_name}
                                                    </Link>
                                                </td>

                                                {/* <td style={styles.modalTd}>
                                                    {toDMY(item.production_start_date)}
                                                </td>

                                                <td style={styles.modalTd}>
                                                    {toDMY(item.production_end_date)}
                                                </td> */}

                                                <td style={styles.modalTd}>
                                                    <Link
                                                        to={`/cable/product/product-code?view=${encodeURIComponent(
                                                            item.product_code
                                                        )}&returnTo=${encodeURIComponent(
                                                            location.pathname + location.search
                                                        )}`}
                                                        style={styles.cellLink}
                                                    >
                                                        {item.product_code}
                                                    </Link>
                                                </td>

                                                <td style={styles.modalTd}>
                                                    {item.description}
                                                </td>

                                                <td style={styles.modalTd}>
                                                    {item.uom}
                                                </td>

                                                <td style={styles.modalTd}>
                                                    {formatQty(item.qty)}
                                                </td>

                                                {modalType === "request" ? (
                                                    <>
                                                        <td style={styles.modalTd}>
                                                            <input
                                                                className="form-control"
                                                                style={{
                                                                    ...styles.modalInput,
                                                                    ...(requestAttemptedSave &&
                                                                        (!item.request_qty || Number(item.request_qty) <= 0)
                                                                        ? { borderColor: "#dc2626", background: "#fef2f2" }
                                                                        : {}),
                                                                }}
                                                                type="number"
                                                                value={item.request_qty}
                                                                onChange={(e) => {
                                                                    const temp = [...requestItems];
                                                                    temp[index].request_qty = e.target.value;
                                                                    setRequestItems(temp);
                                                                }}
                                                            />
                                                            {requestAttemptedSave &&
                                                                (!item.request_qty || Number(item.request_qty) <= 0) && (
                                                                    <div style={{ color: "#dc2626", fontSize: 11, marginTop: 2 }}>
                                                                        Required
                                                                    </div>
                                                                )}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            <input
                                                                className="form-control"
                                                                style={styles.modalDateInput}
                                                                type="date"
                                                                value={item.request_date}
                                                                onChange={(e) => {
                                                                    const temp = [...requestItems];
                                                                    temp[index].request_date = e.target.value;
                                                                    setRequestItems(temp);
                                                                }}
                                                            />
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            {(availableStocks[index] || []).length > 0 ? (
                                                                <span
                                                                    style={styles.cellLink}
                                                                    onClick={() =>
                                                                        openStockModal(
                                                                            item.product_code,
                                                                            item.description,
                                                                            `${item.storage_code} - ${item.storage_location}`,
                                                                            availableStocks[index]
                                                                        )
                                                                    }
                                                                >
                                                                    {formatQty(sumQty(availableStocks[index]))}
                                                                </span>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            <Select
                                                                styles={
                                                                    requestAttemptedSave && !item.storage_location
                                                                        ? {
                                                                            ...modalSelectStyles,
                                                                            control: (base, state) => ({
                                                                                ...(modalSelectStyles?.control
                                                                                    ? modalSelectStyles.control(base, state)
                                                                                    : base),
                                                                                borderColor: "#dc2626",
                                                                                background: "#fef2f2",
                                                                            }),
                                                                        }
                                                                        : modalSelectStyles
                                                                }
                                                                placeholder="Select"
                                                                options={storageLocationOptions}
                                                                value={
                                                                    item.storage_code
                                                                        ? storageLocationOptions.find(
                                                                            (opt) => opt.value === item.storage_code
                                                                        )
                                                                        : null
                                                                }
                                                                onChange={(selectedOption) => {
                                                                    const temp = [...requestItems];

                                                                    if (!selectedOption) {
                                                                        temp[index].storage_code = "";
                                                                        temp[index].storage_location = "";
                                                                        setRequestItems(temp);
                                                                        return;
                                                                    }

                                                                    temp[index].storage_code =
                                                                        selectedOption.value;

                                                                    temp[index].storage_location =
                                                                        selectedOption.storage_location;

                                                                    setRequestItems(temp);

                                                                    loadAvailableStock(
                                                                        item.product_code,
                                                                        selectedOption.value,
                                                                        index
                                                                    );
                                                                }}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                            {requestAttemptedSave && !item.storage_location && (
                                                                <div style={{ color: "#dc2626", fontSize: 11, marginTop: 2 }}>
                                                                    Required
                                                                </div>
                                                            )}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td style={styles.modalTd}>
                                                            {formatQty(item.request_qty)}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            {toDMY(item.request_date)}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            {item.to_storage_location ? (
                                                                <span
                                                                    style={styles.cellLink}
                                                                    onClick={() =>
                                                                        openStockModal(
                                                                            item.product_code,
                                                                            item.description,
                                                                            item.to_storage_location,
                                                                            rowAvailableStocks[getRowKey(item)] || []
                                                                        )
                                                                    }
                                                                >
                                                                    {item.to_storage_location}
                                                                </span>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            {(rowAvailableStocks[getRowKey(item)] || []).length > 0
                                                                ? formatQty(
                                                                    sumQty(rowAvailableStocks[getRowKey(item)])
                                                                )
                                                                : "-"}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            {formatQty(item.issued_qty)}
                                                        </td>

                                                        <td style={{ ...styles.modalTd, minWidth: 160 }}>
                                                            {item.batches && item.batches.length > 0 ? (
                                                                <>
                                                                    <div style={{ fontSize: 12 }}>
                                                                        {item.batches[0].batch_number} - Qty:{" "}
                                                                        {formatQty(item.batches[0].issued_qty)}
                                                                    </div>
                                                                    {item.batches.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0"
                                                                            style={{
                                                                                fontSize: 12,
                                                                                fontWeight: 600,
                                                                                textDecoration: "none",
                                                                            }}
                                                                            onClick={() =>
                                                                                openStockModal(
                                                                                    item.product_code,
                                                                                    item.description,
                                                                                    item.from_storage_location,
                                                                                    item.batches
                                                                                )
                                                                            }
                                                                        >
                                                                            +{item.batches.length - 1} more
                                                                        </button>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            <input
                                                                className="form-control"
                                                                style={styles.compactInput}
                                                                type="number"
                                                                value={item.confirm_qty || ""}
                                                                onChange={(e) => {
                                                                    const temp = [...requestItems];
                                                                    temp[index].confirm_qty = e.target.value;
                                                                    setRequestItems(temp);
                                                                }}
                                                            />
                                                        </td>

                                                        <td style={styles.modalTd}>
                                                            <input
                                                                className="form-control"
                                                                style={styles.compactInput}
                                                                type="date"
                                                                value={item.confirm_date || ""}
                                                                onChange={(e) => {
                                                                    const temp = [...requestItems];
                                                                    temp[index].confirm_date = e.target.value;
                                                                    setRequestItems(temp);
                                                                }}
                                                            />
                                                        </td>
                                                    </>
                                                )}

                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setShowModal(false);
                                    setRequestAttemptedSave(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-dark"
                                onClick={
                                    modalType === "request"
                                        ? handleSaveRequest
                                        : handleSaveConfirm
                                }
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Batch Details Modal (Available / Issued Stock) ─── */}
            {batchModalData && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1100 }}
                    onClick={() => setBatchModalData(null)}
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "480px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <div>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                    }}
                                >
                                    {batchModalData.product_code} - {batchModalData.description}
                                </p>
                                {batchModalData.storage_label && (
                                    <p
                                        style={{
                                            margin: "2px 0 0",
                                            fontSize: "12px",
                                            color: "#64748b",
                                        }}
                                    >
                                        {batchModalData.storage_label}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setBatchModalData(null)}
                                style={styles.iconBtn}
                            >
                                ✖
                            </button>
                        </div>

                        <div style={styles.modalBody}>
                            {batchModalData.batches.length > 0 ? (
                                <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Batch No</th>
                                            <th style={styles.erpTh}>Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batchModalData.batches.map((b, i) => (
                                            <tr key={i}>
                                                <td style={styles.erpTd}>{b.batch_number || "-"}</td>
                                                <td style={styles.erpTd}>{formatQty(b.qty ?? b.issued_qty)}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td style={{ ...styles.erpTd, fontWeight: 700 }}>Total</td>
                                            <td style={{ ...styles.erpTd, fontWeight: 700 }}>
                                                {formatQty(sumQty(batchModalData.batches))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                                    No stock available.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}




            {/* ── Goods Request View Modal (header + Product/Docs tabs) ── */}
            {docsModal && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1200 }}
                    onClick={() => setDocsModal(null)}
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "1100px", width: "95%", height: "90vh" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                                Request : {docsModal.requestNo}
                            </p>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <button onClick={() => setDocsModal(null)} style={styles.iconBtn}>✖</button>
                            </div>
                        </div>

                        <div style={{ padding: "16px 20px 0" }}>
                            {/* ── Header fields (fixed, same across tabs) ─────── */}
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(6, 1fr)",
                                    gap: "16px",
                                    marginBottom: "16px",
                                }}
                            >
                                <div>
                                    <label style={styles.compactLabel}>Request No</label>
                                    <div style={styles.readOnlyBox}>{docsModal.requestNo}</div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>Production Order</label>
                                    <div style={styles.readOnlyBox}>
                                        {formatProductionOrders(docsModal.items?.[0]?.production_order_number)}
                                    </div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>Sales Order</label>
                                    <div style={styles.readOnlyBox}>
                                        {formatSalesOrders(docsModal.items?.[0]?.sales_order_numbers)}
                                    </div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>Workcenter</label>
                                    <div style={styles.readOnlyBox}>
                                        {docsModal.items?.[0]?.workcenter_name || "-"}
                                    </div>
                                </div>
                                {/* <div>
                                    <label style={styles.compactLabel}>Start Date</label>
                                    <div style={styles.readOnlyBox}>
                                        {toDMY(docsModal.items?.[0]?.production_start_date) || "-"}
                                    </div>
                                </div>
                                <div>
                                    <label style={styles.compactLabel}>End Date</label>
                                    <div style={styles.readOnlyBox}>
                                        {toDMY(docsModal.items?.[0]?.production_end_date) || "-"}
                                    </div>
                                </div> */}
                            </div>

                            {/* ── Tab toggle: Product / Docs ──────────────────── */}
                            <div className="gr-tab-toggle" style={{ marginBottom: "12px" }}>
                                <button
                                    className={docsModal.activeDetailTab === "product" ? "active-tab" : ""}
                                    onClick={() =>
                                        setDocsModal((prev) => ({ ...prev, activeDetailTab: "product" }))
                                    }
                                >
                                    Product
                                </button>
                                <button
                                    className={docsModal.activeDetailTab === "docs" ? "active-tab" : ""}
                                    onClick={() =>
                                        setDocsModal((prev) => ({ ...prev, activeDetailTab: "docs" }))
                                    }
                                >
                                    Docs
                                </button>
                                <button
                                    className={docsModal.activeDetailTab === "change_log" ? "active-tab" : ""}
                                    onClick={() =>
                                        setDocsModal((prev) => ({ ...prev, activeDetailTab: "change_log" }))
                                    }
                                >
                                    Change Log
                                </button>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 20px" }}>
                            {/* ── Product tab: line-item table ────────────────── */}
                            {docsModal.activeDetailTab === "product" && (
                                <table className="table" style={styles.erpTable}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Description</th>
                                            <th style={styles.erpTh}>UOM</th>
                                            <th style={styles.erpTh}>Actual Qty</th>
                                            <th style={styles.erpTh}>Requested Qty</th>
                                            <th style={styles.erpTh}>Open Qty</th>
                                            <th style={styles.erpTh}>Requested Date</th>
                                            <th style={styles.erpTh}>Available Stock</th>
                                            <th style={styles.erpTh}>To Storage Location</th>
                                            <th style={styles.erpTh}>Issued Qty</th>
                                            <th style={styles.erpTh}>Issued Date</th>
                                            <th style={styles.erpTh}>From Storage Location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(docsModal.items || []).map((item, idx) => {
                                            const stockForItem = detailAvailableStocks[idx] || [];
                                            return (
                                                <tr key={item.id || idx}>
                                                    <td style={styles.erpTd}>{item.product_code}</td>
                                                    <td style={styles.erpTd}>{item.description}</td>
                                                    <td style={styles.erpTd}>{item.uom}</td>
                                                    <td style={styles.erpTd}>{formatQty(item.qty)}</td>
                                                    <td style={styles.erpTd}>{formatQty(item.request_qty)}</td>
                                                    <td style={styles.erpTd}>{formatQty(item.open_qty)}</td>
                                                    <td style={styles.erpTd}>{toDMY(item.request_date) || "-"}</td>
                                                    <td style={styles.erpTd}>
                                                        {stockForItem.length > 0 ? (
                                                            <span
                                                                style={styles.cellLink}
                                                                onClick={() =>
                                                                    openStockModal(
                                                                        item.product_code,
                                                                        item.description,
                                                                        `${item.storage_code} - ${item.storage_location}`,
                                                                        stockForItem
                                                                    )
                                                                }
                                                            >
                                                                {formatQty(sumQty(stockForItem))}
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                    <td style={styles.erpTd}>
                                                        {item.storage_code
                                                            ? `${item.storage_code} - ${item.storage_location}`
                                                            : "-"}
                                                    </td>
                                                    <td style={styles.erpTd}>{formatQty(item.issued_qty)}</td>
                                                    <td style={styles.erpTd}>{toDMY(item.issue_date) || "-"}</td>
                                                    <td style={styles.erpTd}>
                                                        {item.from_storage_location || "-"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}

                            {/* ── Docs tab: file upload row + document card ──── */}
                            {docsModal.activeDetailTab === "docs" && (
                                <div>
                                    <div style={{ display: "flex", gap: "0", marginBottom: "6px" }}>
                                        <label
                                            style={{
                                                fontSize: "13px",
                                                padding: "6px 14px",
                                                background: "#e2e8f0",
                                                border: "1px solid #cbd5e1",
                                                borderRight: "none",
                                                borderRadius: "6px 0 0 6px",
                                                cursor: "pointer",
                                                color: "#1e293b",
                                            }}
                                        >
                                            Choose Files
                                            <input
                                                type="file"
                                                style={{ display: "none" }}
                                                onChange={(e) =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        chosenFileName: e.target.files?.[0]?.name || null,
                                                    }))
                                                }
                                            />
                                        </label>
                                        <div
                                            style={{
                                                fontSize: "13px",
                                                padding: "6px 14px",
                                                border: "1px solid #cbd5e1",
                                                borderRadius: "0 6px 6px 0",
                                                color: "#64748b",
                                                flex: 1,
                                            }}
                                        >
                                            {docsModal.chosenFileName || "No file chosen"}
                                        </div>
                                    </div>
                                    <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "16px" }}>
                                        Supported formats: PDF, DOCX, XLSX
                                    </p>

                                    <div
                                        style={{
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "8px",
                                            padding: "14px 16px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#1e293b",
                                                marginBottom: "10px",
                                            }}
                                        >
                                            <span>📄</span>
                                            <span>{docsModal.requestNo}_GoodsRequest.pdf</span>
                                        </div>
                                        <div style={{ display: "flex", gap: "8px" }}>
                                            <button
                                                className="btn btn-sm btn-dark"
                                                onClick={() =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        previewOpen: true,
                                                        autoPrint: false,
                                                        autoDownload: false,
                                                    }))
                                                }
                                            >
                                                View
                                            </button>
                                            <button
                                                className="btn btn-sm btn-dark"
                                                onClick={() =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        previewOpen: true,
                                                        autoDownload: true,
                                                        autoPrint: false,
                                                    }))
                                                }
                                            >
                                                Download
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() =>
                                                    setDocsModal((prev) => ({
                                                        ...prev,
                                                        previewOpen: true,
                                                        autoPrint: true,
                                                        autoDownload: false,
                                                    }))
                                                }
                                            >
                                                Print
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Change Log tab: field-level audit trail ─────── */}
                            {docsModal.activeDetailTab === "change_log" && (
                                <table className="table" style={styles.erpTable}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Date & Time</th>
                                            <th style={styles.erpTh}>User</th>
                                            <th style={styles.erpTh}>Action</th>
                                            <th style={styles.erpTh}>Field</th>
                                            <th style={styles.erpTh}>Old Value</th>
                                            <th style={styles.erpTh}>New Value</th>
                                            <th style={styles.erpTh}>Comment</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {changeLogs.length > 0 ? (
                                            changeLogs.map((log, idx) => {
                                                const dateVal = log.created_at || log.updated_at;
                                                return (
                                                    <tr key={idx}>
                                                        <td style={styles.erpTd}>
                                                            {(() => {
                                                                const d = new Date(dateVal);
                                                                if (isNaN(d.getTime())) return "";
                                                                const dd = String(d.getDate()).padStart(2, "0");
                                                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                                                const yyyy = d.getFullYear();
                                                                const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                                                                return `${dd}/${mm}/${yyyy}, ${time}`;
                                                            })()}
                                                        </td>
                                                        <td style={styles.erpTd}>{log.created_by || log.updated_by || "System"}</td>
                                                        <td style={styles.erpTd}>{log.action_type || "Update"}</td>
                                                        <td style={styles.erpTd}>
                                                            {log.field_name
                                                                ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                                                : '-'}
                                                        </td>
                                                        <td style={{ ...styles.erpTd, maxWidth: 150 }}>{log.old_value || "-"}</td>
                                                        <td style={{ ...styles.erpTd, maxWidth: 150 }}>{log.new_value || "-"}</td>
                                                        <td style={{ ...styles.erpTd, maxWidth: 200, wordWrap: "break-word" }}>{log.comment || "-"}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", padding: 20 }}>
                                                    No changes recorded
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Full PDF Preview Modal (opened from the Docs tab's
                    document card) ─────────────────────────────────── */}
            {docsModal?.previewOpen && (
                <div
                    style={{ ...styles.modalBackdrop, zIndex: 1300 }}
                    onClick={() =>
                        setDocsModal((prev) => ({ ...prev, previewOpen: false }))
                    }
                >
                    <div
                        style={{ ...styles.modal, maxWidth: "900px", width: "95%", height: "90vh" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={styles.modalHeader}>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <span>📄</span>
                                <span>{docsModal.requestNo}_GoodsRequest.pdf</span>
                            </p>
                            <button
                                className="btn btn-sm"
                                style={{
                                    fontSize: "12px",
                                    color: "#dc2626",
                                    border: "1px solid #dc2626",
                                    background: "#fff",
                                    borderRadius: "6px",
                                    padding: "4px 12px",
                                }}
                                onClick={() =>
                                    setDocsModal((prev) => ({ ...prev, previewOpen: false }))
                                }
                            >
                                ✖ Close
                            </button>
                        </div>

                        <div style={{ position: "relative", flex: 1, overflowY: "auto" }}>
                            <button
                                className="btn btn-sm btn-dark"
                                style={{
                                    position: "absolute",
                                    top: "12px",
                                    right: "20px",
                                    fontSize: "12px",
                                    zIndex: 2,
                                }}
                                onClick={() => downloadGoodsRequest(`${docsModal.requestNo}.pdf`)}
                            >
                                Download PDF
                            </button>
                            <div style={{ padding: "16px" }}>
                                <GoodsRequestForm requestNo={docsModal.requestNo} />
                            </div>
                        </div>
                    </div>
                </div>
            )}


            <style>{`
.gr-tab-toggle {
    display: inline-flex;
    background: #f1f5f9;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
}
.gr-tab-toggle button {
    border: none;
    background: transparent;
    padding: 6px 18px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
    transition: .2s;
}
.gr-tab-toggle button.active-tab {
    background: #fff;
    color: #1e293b;
    box-shadow: 0 1px 3px rgba(0,0,0,.15);
}
`}</style>
        </Layout>
    );
}

export default GoodsRequest;